/**
 * End-of-run and game-over cards for the TV's shared summary layout
 * (tv/summary.ts TvSummarySection), 2026-09-30 sweep: every mode that ends a
 * round with a TV connected shows the round's stats on the TV, the TV's
 * "Continue on your device." prompt, and VIEW STATS ON PHONE on the remote.
 *
 * Two shapes cover the modes that had only a streak card, a round-over card,
 * or nothing at all:
 *
 * - `runCardTvLayout`: one player's run (Start Line streak, Daily Start Line,
 *   the solo Putting Ladder). A hero number, stat tiles, and, where the mode
 *   has them, the run's start-line chart and a leaderboard.
 * - `versusGameTvLayout`: a two-player game (Line Four, Turf War, Ladder
 *   Duel, HORSE). The result across the top, each player's numbers, and the
 *   final board when the game has one (`lineFourBoardGrid`,
 *   `turfWarZonesGrid`).
 *
 * Pure: colors come in as arguments (the phone's theme tokens), so node tests
 * and the TV page's fixtures import it directly.
 */
import type { LineFourState } from '../multiplayerGames/lineFour';
import { LINE_FOUR_COLUMN_LABELS, LINE_FOUR_COLUMNS, LINE_FOUR_ROWS } from '../multiplayerGames/lineFour';
import type { TurfWarZoneStanding } from '../multiplayerGames/turfWar';
import { TURF_WAR_ZONES } from '../multiplayerGames/turfWar';
import type {
  TvSummary,
  TvSummaryEntriesSection,
  TvSummaryGridSection,
  TvSummaryMetricsSection,
  TvSummaryPuttsSection,
  TvSummarySection,
  TvSummaryStartLinesSection,
  TvSummaryText,
} from './summary';
import { tvText, type TvSummaryLayout } from './summarySections';

/** The theme colors these cards use. */
export interface TvGameOverColors {
  gold: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
}

type Tile = TvSummaryMetricsSection['tiles'][number];

/** Mean absolute start line of the putts that have one, or null. */
export function averageAbsAngleDeg(angles: ReadonlyArray<number | null | undefined>): number | null {
  const valid = angles.filter((angle): angle is number => typeof angle === 'number' && Number.isFinite(angle));
  if (!valid.length) return null;
  return valid.reduce((sum, angle) => sum + Math.abs(angle), 0) / valid.length;
}

/** "0.84°", or "--" with no start lines. */
export function formatAverageAngle(angle: number | null): string {
  return angle === null || !Number.isFinite(angle) ? '--' : `${angle.toFixed(2)}°`;
}

/* ---------------------------------------------------------------------------
 * One player's run
 * ------------------------------------------------------------------------ */

export interface RunCardTvInput {
  /** "20 FT STREAK", "DAILY START LINE · 12 FT", "PUTTING LADDER · 10–40 FT". */
  eyebrow: string;
  /** The hero number ("7"). */
  value: string;
  /** Under the number ("IN A ROW", "CLIMBS"). */
  valueLabel: string;
  /** A verdict line ("NEW PERSONAL BEST", "PRACTICE ONLY. NOT RANKED."). */
  detail?: string | null;
  detailColor?: string | null;
  /** Lines under the verdict, inside the hero card (the round's place on The
   *  LagKing's board this month, lagking/MonthlyPlace.tsx). */
  extra?: TvSummaryText[] | null;
  tiles: Tile[];
  /** A leaderboard beside the stats (the run's board, the day's board). */
  board?: { title: string; rows: TvSummaryEntriesSection['rows'] } | null;
  /** A putt-by-putt list (the Ladder's climbs), beside the stats. */
  putts?: Pick<TvSummaryPuttsSection, 'header' | 'rows'> | null;
  /** The run's start-line chart; omitted for a mode without start lines. */
  startLines?: { title: string; angles: Array<number | null>; palette: TvSummaryStartLinesSection['palette'] } | null;
  colors: TvGameOverColors;
}

export function runCardTvLayout(input: RunCardTvInput): TvSummaryLayout {
  const { colors } = input;
  const hero: TvSummaryText[] = [
    tvText(input.eyebrow, colors.textMuted, 'caption', 'bold', 0.15),
    tvText(input.value, colors.gold, 'hero', 'heavy'),
    tvText(input.valueLabel, colors.textPrimary, 'body', 'heavy', 0.1),
    ...(input.detail ? [tvText(input.detail, input.detailColor ?? colors.gold, 'body', 'bold', 0.08)] : []),
    ...(input.extra ?? []),
  ];
  const sections: TvSummarySection[] = [
    { kind: 'text', column: 0, card: true, align: 'center', lines: hero },
  ];
  if (input.tiles.length) {
    sections.push({
      kind: 'metrics',
      column: 0,
      perRow: 2,
      tiles: input.tiles.map((tile) => ({ labelColor: colors.textSecondary, subColor: colors.textMuted, ...tile })),
    });
  }
  let column = 1;
  if (input.putts && input.putts.rows.length) {
    sections.push({ kind: 'putts', column, card: true, ...input.putts });
    column += 1;
  }
  if (input.startLines) {
    sections.push({
      kind: 'startLines',
      column,
      title: input.startLines.title,
      angles: [...input.startLines.angles],
      palette: input.startLines.palette,
    });
    column += 1;
  }
  if (input.board && input.board.rows.length) {
    sections.push({ kind: 'entries', column, title: input.board.title, rows: input.board.rows });
    column += 1;
  }
  const columns = column === 1 ? [1] : column === 2 ? [1, 1.1] : column === 3 ? [0.95, 1.1, 1] : [0.9, 1, 1, 1];
  return { sections, columns };
}

/** Leaderboard rows for a run card: rank, name, value; the player's row in gold. */
export function runBoardRows(
  rows: ReadonlyArray<{ rank: number | null; name: string; value: string; you?: boolean; thisRun?: boolean }>,
  colors: TvGameOverColors,
): TvSummaryEntriesSection['rows'] {
  return rows.map((row) => {
    const highlight = row.you || row.thisRun;
    return {
      rank: row.rank == null ? '-' : String(row.rank),
      rankColor: colors.textMuted,
      name: row.name,
      nameColor: highlight ? colors.gold : colors.textPrimary,
      value: row.value,
      valueColor: highlight ? colors.gold : colors.textPrimary,
      border: row.thisRun ? colors.gold : null,
    };
  });
}

/* ---------------------------------------------------------------------------
 * Two-player games
 * ------------------------------------------------------------------------ */

export interface VersusPlayerTv {
  name: string;
  color: string;
  /** The game's score for this player ("3", "HO", "12"). */
  value: string;
  /** Caption under it ("ZONES", "LETTERS", "PTS"). */
  valueLabel: string;
  /** Key numbers ("6 putts · 3 landed in turf"). */
  detail?: string | null;
  winner?: boolean;
  /** Mark the losing side with an L beside the winner's W (Baseball's box score). */
  loser?: boolean;
}

export interface VersusGameTvInput {
  /** "LINE FOUR", "TURF WAR · SUDDEN DEATH". */
  eyebrow: string;
  /** "COLE WINS", "DRAW". */
  title: string;
  titleColor?: string | null;
  /** "3–2 ZONES", "Kelly spelled HORSE". */
  detail?: string | null;
  players: VersusPlayerTv[];
  /** The final board, drawn beside the players. */
  board?: { title: string; grid: Omit<TvSummaryGridSection, 'kind' | 'column' | 'title'> } | null;
  /** Lines under the players ("Loser leads the next round"). */
  notes?: TvSummaryText[] | null;
  colors: TvGameOverColors;
}

export function versusGameTvLayout(input: VersusGameTvInput): TvSummaryLayout {
  const { colors } = input;
  // Without a board the players sit in a centered middle column, not
  // stretched across the screen.
  const main = input.board ? 0 : 1;
  const sections: TvSummarySection[] = [
    {
      kind: 'text',
      band: 'top',
      column: 0,
      align: 'center',
      lines: [
        tvText(input.eyebrow, colors.textMuted, 'caption', 'bold', 0.15),
        tvText(input.title, input.titleColor ?? colors.gold, 'value', 'heavy', 0.03),
        ...(input.detail ? [tvText(input.detail, colors.textSecondary, 'body', 'bold', 0.06)] : []),
      ],
    },
    {
      kind: 'entries',
      column: main,
      title: 'PLAYERS',
      rows: input.players.map((player) => ({
        rank: player.winner ? 'W' : player.loser ? 'L' : null,
        rankColor: player.winner ? colors.gold : colors.textMuted,
        name: player.name,
        nameColor: player.color,
        detail: player.detail ?? null,
        detailColor: colors.textSecondary,
        value: player.value,
        valueColor: player.winner ? colors.gold : colors.textPrimary,
        valueLabel: player.valueLabel,
        border: player.winner ? colors.gold : null,
      })),
    },
  ];
  if (input.notes?.length) {
    sections.push({ kind: 'text', column: main, align: 'center', lines: input.notes });
  }
  if (input.board) {
    sections.push({ kind: 'grid', column: 1, title: input.board.title, ...input.board.grid });
  }
  return { sections, columns: input.board ? [1, 1] : [0.55, 1, 0.55] };
}

/** "6 putts", "1 putt". */
export function puttCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'putt' : 'putts'}`;
}

/* ---- Line Four ---- */

/** Pieces each player has on the board. */
export function lineFourPieces(state: Pick<LineFourState, 'board'>): [number, number] {
  const pieces: [number, number] = [0, 0];
  for (const row of state.board) for (const cell of row) if (cell === 0 || cell === 1) pieces[cell] += 1;
  return pieces;
}

/** The final 7 x 6 board, top row first, the winning line ringed. */
export function lineFourBoardGrid(
  state: Pick<LineFourState, 'board' | 'winningCells'>,
  playerColors: readonly [string, string],
  palette: TvSummaryGridSection['palette'],
  ringColor: string,
): Omit<TvSummaryGridSection, 'kind' | 'column' | 'title'> {
  const winning = new Set(state.winningCells.map((cell) => `${cell.row}:${cell.column}`));
  const cells: TvSummaryGridSection['cells'] = [];
  // Row 0 is the bottom of the board; the TV reads top to bottom.
  for (let row = LINE_FOUR_ROWS - 1; row >= 0; row -= 1) {
    for (let column = 0; column < LINE_FOUR_COLUMNS; column += 1) {
      const owner = state.board[row]?.[column] ?? null;
      cells.push({
        fill: owner === 0 || owner === 1 ? playerColors[owner] : null,
        ...(winning.has(`${row}:${column}`) ? { ring: ringColor } : {}),
      });
    }
  }
  return { columns: LINE_FOUR_COLUMNS, cells, columnLabels: [...LINE_FOUR_COLUMN_LABELS], shape: 'disc', palette };
}

/* ---- Turf War ---- */

/** Each zone: who controls it (tinted in their color) and the landed counts. */
export function turfWarZonesGrid(
  standings: ReadonlyArray<Pick<TurfWarZoneStanding, 'zoneIndex' | 'counts' | 'controllerIndex'>>,
  playerColors: readonly [string, string],
  palette: TvSummaryGridSection['palette'],
  suddenDeathZoneIndex: number | null,
  ringColor: string,
): Omit<TvSummaryGridSection, 'kind' | 'column' | 'title'> {
  const cells: TvSummaryGridSection['cells'] = TURF_WAR_ZONES.map((zone) => {
    const standing = standings.find((item) => item.zoneIndex === zone.index);
    const counts = standing?.counts ?? [0, 0];
    const controller = standing?.controllerIndex ?? null;
    return {
      fill: controller === null ? null : playerColors[controller],
      text: `${counts[0]}–${counts[1]}`,
      sub: `${zone.label} FT`,
      ...(suddenDeathZoneIndex === zone.index ? { ring: ringColor } : {}),
    };
  });
  return { columns: TURF_WAR_ZONES.length, cells, shape: 'box', palette };
}

/** Putts and landed-in-turf counts per player, from the engine's shot log. */
export function turfWarPlayerShots(
  shots: ReadonlyArray<{ playerIndex: 0 | 1; zoneIndex: number | null }>,
): Array<{ putts: number; landed: number }> {
  return [0, 1].map((player) => {
    const mine = shots.filter((shot) => shot.playerIndex === player);
    return { putts: mine.length, landed: mine.filter((shot) => shot.zoneIndex !== null).length };
  });
}

/* ---------------------------------------------------------------------------
 * Each game's card, so the hosts and the TV page's fixtures build the same
 * payload.
 * ------------------------------------------------------------------------ */

/** Colors the game-over cards add to the run-card set. */
export interface TvVersusColors extends TvGameOverColors {
  /** The board's empty cell and card chrome. */
  empty: string;
  line: string;
  /** Ring around the winning line or the sudden-death zone. */
  ring: string;
}

function gridPalette(colors: TvVersusColors): TvSummaryGridSection['palette'] {
  return { empty: colors.empty, line: colors.line, label: colors.textMuted };
}

/** Line Four game over: winner, the final board, each player's pieces and putts. */
export function lineFourGameOverTv(input: {
  names: readonly [string, string];
  playerColors: readonly [string, string];
  state: Pick<LineFourState, 'board' | 'winningCells' | 'winnerIndex' | 'isDraw' | 'attemptCount'>;
  /** Putts each player took (misses and full columns included). */
  putts: readonly [number, number];
  startLineMode?: boolean;
  colors: TvVersusColors;
}): TvSummary {
  const { state, names, colors } = input;
  const pieces = lineFourPieces(state);
  const title = state.isDraw ? 'DRAW' : `${names[state.winnerIndex ?? 0].toUpperCase()} WINS`;
  const eyebrow = input.startLineMode ? 'LINE FOUR · START LINE' : 'LINE FOUR';
  return {
    kicker: eyebrow,
    title,
    headline: `${state.attemptCount} PUTTS`,
    rows: [],
    ...versusGameTvLayout({
      eyebrow,
      title,
      detail: state.isDraw ? 'The board filled with no four in a row' : `Four in a row in ${state.attemptCount} putts`,
      players: [0, 1].map((index) => ({
        name: names[index],
        color: input.playerColors[index],
        value: String(pieces[index]),
        valueLabel: 'PIECES',
        detail: `${puttCountLabel(input.putts[index])} · ${Math.max(0, input.putts[index] - pieces[index])} missed`,
        winner: !state.isDraw && state.winnerIndex === index,
      })),
      board: { title: 'FINAL BOARD', grid: lineFourBoardGrid(state, input.playerColors, gridPalette(colors), colors.ring) },
      colors,
    }),
  };
}

/** Turf War game over: winner, the zones, each player's putts and landings. */
export function turfWarGameOverTv(input: {
  names: readonly [string, string];
  playerColors: readonly [string, string];
  score: readonly [number, number];
  winnerIndex: 0 | 1;
  standings: ReadonlyArray<Pick<TurfWarZoneStanding, 'zoneIndex' | 'counts' | 'controllerIndex'>>;
  shots: ReadonlyArray<{ playerIndex: 0 | 1; zoneIndex: number | null }>;
  suddenDeathZoneIndex: number | null;
  colors: TvVersusColors;
}): TvSummary {
  const { names, colors } = input;
  const shots = turfWarPlayerShots(input.shots);
  const title = `${names[input.winnerIndex].toUpperCase()} WINS`;
  const eyebrow = input.suddenDeathZoneIndex !== null ? 'TURF WAR · SUDDEN DEATH' : 'TURF WAR · MOST TURF CONTROLLED';
  return {
    kicker: eyebrow,
    title,
    headline: `${input.score[0]}–${input.score[1]} ZONES`,
    rows: [],
    ...versusGameTvLayout({
      eyebrow,
      title,
      titleColor: input.playerColors[input.winnerIndex],
      detail: `${input.score[0]}–${input.score[1]} zones`,
      players: [0, 1].map((index) => ({
        name: names[index],
        color: input.playerColors[index],
        value: String(input.score[index]),
        valueLabel: 'ZONES',
        detail: `${puttCountLabel(shots[index].putts)} · ${shots[index].landed} landed in turf`,
        winner: input.winnerIndex === index,
      })),
      board: {
        title: 'FINAL TURF',
        grid: turfWarZonesGrid(input.standings, input.playerColors, gridPalette(colors), input.suddenDeathZoneIndex, colors.ring),
      },
      colors,
    }),
  };
}

/** Ladder Duel round over: who took the pot, the running score, each
 *  player's climbs and putts this match. Counts down to the next round. */
export function ladderDuelRoundOverTv(input: {
  names: readonly [string, string];
  playerColors: readonly [string, string];
  roundNumber: number;
  /** "TOP OUT!" / "ROUND OVER". */
  eyebrow: string;
  /** "Cole hit it short". */
  headline: string;
  winnerIndex: 0 | 1;
  pot: number;
  scores: readonly [number, number];
  /** This match so far. */
  climbs: readonly [number, number];
  putts: readonly [number, number];
  rangeLabel: string;
  colors: TvGameOverColors;
}): TvSummary {
  const { names, colors } = input;
  const title = `${names[input.winnerIndex].toUpperCase()} TAKES THE POT · +${input.pot}`;
  return {
    kicker: input.eyebrow,
    title: input.headline,
    rows: [],
    ...versusGameTvLayout({
      eyebrow: `LADDER 1V1 · ROUND ${input.roundNumber} · ${input.eyebrow}`,
      title,
      detail: `${input.headline} · ${input.rangeLabel}`,
      players: [0, 1].map((index) => ({
        name: names[index],
        color: input.playerColors[index],
        value: String(input.scores[index]),
        valueLabel: 'PTS',
        detail: `${input.climbs[index]} ${input.climbs[index] === 1 ? 'climb' : 'climbs'} · ${puttCountLabel(input.putts[index])}`,
        winner: input.scores[index] > input.scores[1 - index],
      })),
      notes: [tvText('The loser of the round leads the next one.', colors.textMuted, 'caption', 'bold')],
      colors,
    }),
  };
}

/** HORSE game over: winner, each player's letters, makes, and hits. */
export function horseGameOverTv(input: {
  names: readonly [string, string];
  playerColors: readonly [string, string];
  word: string;
  loserIndex: 0 | 1;
  /** Letters spelled so far ("HO"). */
  spelled: readonly [string, string];
  tallies: ReadonlyArray<{ putts: number; makes: number; hits: number }>;
  colors: TvGameOverColors;
}): TvSummary {
  const { names, colors } = input;
  const winner = (1 - input.loserIndex) as 0 | 1;
  const title = `${names[winner].toUpperCase()} WINS`;
  return {
    kicker: `${names[input.loserIndex].toUpperCase()} SPELLED ${input.word}`,
    title,
    rows: [],
    ...versusGameTvLayout({
      eyebrow: input.word,
      title,
      detail: `${names[input.loserIndex]} spelled ${input.word}`,
      players: [0, 1].map((index) => {
        const tally = input.tallies[index] ?? { putts: 0, makes: 0, hits: 0 };
        return {
          name: names[index],
          color: input.playerColors[index],
          value: input.spelled[index] || '--',
          valueLabel: 'LETTERS',
          detail: `${tally.makes} ${tally.makes === 1 ? 'make' : 'makes'} · ${tally.hits} ${tally.hits === 1 ? 'hit' : 'hits'} · ${puttCountLabel(tally.putts)}`,
          winner: index === winner,
        };
      }),
      colors,
    }),
  };
}

/** A side's name in a box score: whole, capped so a very long name still fits its cell. */
export function boxScoreName(name: string): string {
  return name.trim().toUpperCase().slice(0, 12);
}

/**
 * Baseball game over as a box score (Cole, 2026-10-08): the line score by
 * inning with runs and hits, then each side with its W or L and what it did
 * (hits, blocks: hits it took away in the field, double plays turned).
 */
export function baseballGameOverTv(input: {
  /** Away first, then home. */
  names: readonly [string, string];
  playerColors: readonly [string, string];
  runs: readonly [number, number];
  lineScore: readonly [readonly number[], readonly number[]];
  hits: readonly [number, number];
  blocks: readonly [number, number];
  doublePlays: readonly [number, number];
  winnerIndex: 0 | 1;
  /** Innings played; more than the scheduled count means extras. */
  inningsPlayed: number;
  scheduledInnings: number;
  swingOff: boolean;
  colors: TvGameOverColors;
}): TvSummary {
  const { names, colors } = input;
  const title = `${names[input.winnerIndex].toUpperCase()} WINS`;
  const extras = input.swingOff ? 'SWING-OFF' : input.inningsPlayed > input.scheduledInnings ? `F/${input.inningsPlayed}` : 'FINAL';
  const eyebrow = `BASEBALL · ${extras}`;
  // Winner first, the way a final reads.
  const loser = input.winnerIndex === 0 ? 1 : 0;
  const final = `${input.runs[input.winnerIndex]}–${input.runs[loser]}`;
  const frames = Math.max(input.scheduledInnings, input.inningsPlayed, input.lineScore[0].length, input.lineScore[1].length);
  const count = (value: number, one: string, many: string) => `${value} ${value === 1 ? one : many}`;
  const cells = ([0, 1] as const).flatMap((team) => [
    // The whole name (Cole, 2026-11-01: four letters cut "Logan" to LOGA); the TV fits it to the cell.
    { text: boxScoreName(names[team]), textColor: input.playerColors[team], sub: team === 0 ? 'AWAY' : 'HOME' },
    // A frame the side never batted in (a home team already ahead) is an X.
    ...Array.from({ length: frames }, (_, index) => ({
      text: input.lineScore[team][index] === undefined ? 'X' : String(input.lineScore[team][index]),
      textColor: input.lineScore[team][index] === undefined ? colors.textMuted : colors.textPrimary,
    })),
    { text: String(input.runs[team]), textColor: colors.gold, ...(team === input.winnerIndex ? { ring: colors.gold } : {}) },
    { text: String(input.hits[team]), textColor: colors.textPrimary },
  ]);
  return {
    kicker: eyebrow,
    title,
    headline: final,
    rows: [],
    ...versusGameTvLayout({
      eyebrow,
      title,
      titleColor: input.playerColors[input.winnerIndex],
      detail: input.swingOff ? `${final}, decided by a swing-off` : final,
      players: ([0, 1] as const).map((team) => ({
        name: `${names[team]} · ${team === 0 ? 'AWAY' : 'HOME'}`,
        color: input.playerColors[team],
        value: String(input.runs[team]),
        valueLabel: 'RUNS',
        detail: [
          count(input.hits[team], 'hit', 'hits'),
          count(input.blocks[team], 'block', 'blocks'),
          ...(input.doublePlays[team] ? [count(input.doublePlays[team], 'double play', 'double plays')] : []),
        ].join(' · '),
        winner: input.winnerIndex === team,
        loser: input.winnerIndex !== team,
      })),
      board: {
        title: 'BOX SCORE',
        grid: {
          columns: frames + 3,
          columnLabels: ['', ...Array.from({ length: frames }, (_, index) => String(index + 1)), 'R', 'H'],
          cells,
          shape: 'box',
          palette: { empty: 'rgba(255,255,255,0.06)', line: 'rgba(255,255,255,0.18)', label: colors.textSecondary },
        },
      },
      colors,
    }),
  };
}

/**
 * Football game over as a box score (app FOOTBALL_DESIGN_PROPOSAL.md, phase 3):
 * each side's drives in order (the points a drive put up, or what ended it),
 * then the points, with yards, touchdowns, field goals and turnovers under
 * each side's name. The phone's FootballOverlay shows the same.
 */
export function footballGameOverTv(input: {
  /** Away first, then home. */
  names: readonly [string, string];
  playerColors: readonly [string, string];
  scores: readonly [number, number];
  /** Each side's drives: "7", "3", "PUNT", "INT", ... */
  drives: readonly [readonly string[], readonly string[]];
  yards: readonly [number, number];
  touchdowns: readonly [number, number];
  fieldGoals: readonly [number, number];
  turnovers: readonly [number, number];
  winnerIndex: 0 | 1;
  /** Overtime rounds played (0 in regulation). */
  overtime: number;
  colors: TvGameOverColors;
}): TvSummary {
  const { names, colors } = input;
  const title = `${names[input.winnerIndex].toUpperCase()} WINS`;
  const eyebrow = `FOOTBALL · ${input.overtime > 0 ? `FINAL · OT${input.overtime > 1 ? ` ${input.overtime}` : ''}` : 'FINAL'}`;
  const loser = input.winnerIndex === 0 ? 1 : 0;
  const final = `${input.scores[input.winnerIndex]}–${input.scores[loser]}`;
  const frames = Math.max(input.drives[0].length, input.drives[1].length, 1);
  const count = (value: number, one: string, many: string) => `${value} ${value === 1 ? one : many}`;
  const cells = ([0, 1] as const).flatMap((team) => [
    { text: boxScoreName(names[team]), textColor: input.playerColors[team], sub: team === 0 ? 'AWAY' : 'HOME' },
    // A drive the side never had (home skipped its last possession) is an X.
    ...Array.from({ length: frames }, (_, index) => {
      const drive = input.drives[team][index];
      return {
        text: drive === undefined ? 'X' : drive,
        textColor: drive === undefined ? colors.textMuted : /^\d+$/.test(drive) && drive !== '0' ? colors.gold : colors.textSecondary,
      };
    }),
    { text: String(input.scores[team]), textColor: colors.gold, ...(team === input.winnerIndex ? { ring: colors.gold } : {}) },
  ]);
  return {
    kicker: eyebrow,
    title,
    headline: final,
    rows: [],
    ...versusGameTvLayout({
      eyebrow,
      title,
      titleColor: input.playerColors[input.winnerIndex],
      detail: input.overtime > 0 ? `${final} in overtime` : final,
      players: ([0, 1] as const).map((team) => ({
        name: `${names[team]} · ${team === 0 ? 'AWAY' : 'HOME'}`,
        color: input.playerColors[team],
        value: String(input.scores[team]),
        valueLabel: 'POINTS',
        detail: [
          `${input.yards[team]} yds`,
          count(input.touchdowns[team], 'TD', 'TDs'),
          count(input.fieldGoals[team], 'FG', 'FGs'),
          count(input.turnovers[team], 'turnover', 'turnovers'),
        ].join(' · '),
        winner: input.winnerIndex === team,
        loser: input.winnerIndex !== team,
      })),
      board: {
        title: 'DRIVE BY DRIVE',
        grid: {
          columns: frames + 2,
          columnLabels: ['', ...Array.from({ length: frames }, (_, index) => `D${index + 1}`), 'PTS'],
          cells,
          shape: 'box',
          palette: { empty: 'rgba(255,255,255,0.06)', line: 'rgba(255,255,255,0.18)', label: colors.textSecondary },
        },
      },
      colors,
    }),
  };
}

/**
 * Darts-Cricket game over (DARTS_CRICKET.md): the winner, every player's
 * points and marks, and the final marks board (a row per number, darts
 * marks per player; a number closed for everyone is crossed out).
 */
export function cricketGameOverTv(input: {
  names: readonly string[];
  playerColors: readonly string[];
  points: readonly number[];
  /** Marks toward closing, 0 to 21, per player. */
  totalMarks: readonly number[];
  /** One row per target in board order. */
  table: ReadonlyArray<{ label: string; marks: readonly number[]; closed: boolean }>;
  winnerIndex: number | null;
  draw: boolean;
  /** "CLOSED THEM ALL", "MOST POINTS AFTER THE LAST ROUND", "CLINCHED". */
  endedLine: string;
  colors: TvVersusColors;
}): TvSummary {
  const { names, colors } = input;
  const title = input.draw || input.winnerIndex === null ? 'DRAW' : `${names[input.winnerIndex].toUpperCase()} WINS`;
  const eyebrow = `DARTS: CRICKET · ${input.endedLine || 'FINAL'}`;
  const glyph = (count: number) => (count >= 3 ? 'Ⓧ' : count === 2 ? 'X' : count === 1 ? '/' : '');
  const cells = input.table.flatMap((row) => [
    { text: row.label, textColor: row.closed ? colors.textMuted : colors.gold, ...(row.closed ? { sub: 'CLOSED' } : {}) },
    ...names.map((_, player) => ({ text: glyph(row.marks[player] ?? 0), textColor: row.closed ? colors.textMuted : input.playerColors[player] })),
  ]);
  return {
    kicker: eyebrow,
    title,
    headline: input.points.join('–'),
    rows: [],
    ...versusGameTvLayout({
      eyebrow,
      title,
      titleColor: input.winnerIndex !== null && !input.draw ? input.playerColors[input.winnerIndex] : colors.gold,
      detail: `${input.points.join('–')} points`,
      players: names.map((name, player) => ({
        name,
        color: input.playerColors[player],
        value: String(input.points[player] ?? 0),
        valueLabel: 'PTS',
        detail: `${input.totalMarks[player] ?? 0} of 21 marks`,
        winner: !input.draw && input.winnerIndex === player,
      })),
      board: {
        title: 'MARKS',
        grid: {
          columns: names.length + 1,
          cells,
          columnLabels: ['', ...names.map((name) => name.slice(0, 6).toUpperCase())],
          shape: 'box',
          palette: gridPalette(colors),
        },
      },
      colors,
    }),
  };
}
